import { getAuthorizeSubscription } from "utils/subscriptionProject";

function isPresent(value) {
  return value !== null && value !== undefined && value !== "";
}

function unwrapSubscription(subscription) {
  if (!subscription || typeof subscription !== "object" || Array.isArray(subscription)) {
    return null;
  }
  if (
    subscription.subscriptions &&
    typeof subscription.subscriptions === "object" &&
    !Array.isArray(subscription.subscriptions)
  ) {
    return subscription.subscriptions;
  }
  return subscription;
}

/**
 * /subscription/current often sends `{}` instead of null when there is no plan.
 * `{}` is truthy, so the dashboard used to render a fake Free plan.
 * A real record has tariff/name/sum/dates (including canceled Standard).
 */
export function hasDashboardSubscription(subscription) {
  const inner = unwrapSubscription(subscription);
  if (!inner) {
    return false;
  }

  return [
    inner.tariff_plan?.id,
    inner.tariff_plan?.name,
    inner.name,
    inner.sum,
    inner.your_next_billing_date,
    inner.your_next_billing_date_invoice,
    inner.current_billing_date,
    inner.subscription_countries,
    inner.is_active === true ? true : null,
  ].some(isPresent);
}

function getSubscriptionSum(type, sub, currentPlan) {
  const inner = unwrapSubscription(sub) || {};
  switch (type) {
    case "vat":
      return inner.sum ?? currentPlan?.subscriptions?.sum ?? 0;
    case "sales tax":
      return inner.sum ?? currentPlan?.subscriptions_sales_tax?.sum ?? 0;
    case "epr":
      return inner.sum ?? currentPlan?.subscriptions_epr?.sum ?? 0;
    case "omp":
      return inner.sum ?? currentPlan?.subscriptions_omp?.sum ?? 0;
    case "invoice": {
      const invoice = currentPlan?.subscriptions_invoice;
      return (
        inner.sum ??
        invoice?.sum ??
        invoice?.subscriptions?.sum ??
        0
      );
    }
    case "authorize":
      return inner.sum ?? getAuthorizeSubscription(currentPlan)?.sum ?? 0;
    default:
      return inner.sum ?? 0;
  }
}

/**
 * Checks if subscription is paid based on type and subscription data
 * @param {string} type - Subscription type (vat, sales tax, epr, omp)
 * @param {Object} sub - Subscription object
 * @param {Object} currentPlan - Current plan object
 * @returns {boolean} - True if subscription is paid
 */
export const isPaidSubscription = (type, sub, currentPlan) => {
  const inner = unwrapSubscription(sub);
  if (inner?.status === "trial" || inner?.status === "free") {
    return false;
  }

  const sum = Number(getSubscriptionSum(type, sub, currentPlan)) || 0;
  if (type === "invoice" && (inner?.is_active || currentPlan?.subscriptions_invoice?.is_active)) {
    return true;
  }

  return sum > 0;
};

function getProjectSubscription(currentPlan, openProjectName) {
  if (openProjectName === "LTax") {
    return (
      hasDashboardSubscription(currentPlan?.subscriptions) ||
      hasDashboardSubscription(currentPlan?.subscriptions_sales_tax) ||
      hasDashboardSubscription(currentPlan?.subscriptions_omp)
    );
  }
  if (openProjectName === "LEPR") {
    return hasDashboardSubscription(currentPlan?.subscriptions_epr);
  }
  if (openProjectName === "LInvoice") {
    return hasDashboardSubscription(currentPlan?.subscriptions_invoice);
  }
  if (openProjectName === "LAuthorize") {
    return hasDashboardSubscription(getAuthorizeSubscription(currentPlan));
  }
  return false;
}

/**
 * Checks if subscription component should be shown
 * @param {Object} params - Parameters object
 * @param {boolean} params.fetchingPaymentInfo - Is payment info being fetched
 * @param {Object} params.currentPlan - Current plan object
 * @param {string} params.openProjectName - Open project name
 * @returns {boolean} - True if component should be shown
 */
export const shouldShowSubscription = ({ fetchingPaymentInfo, currentPlan, openProjectName }) => {
  return (
    fetchingPaymentInfo ||
    currentPlan?.subscriptions?.status === "trial" ||
    getProjectSubscription(currentPlan, openProjectName)
  );
};
